<template>
  <section>
    <div class="flex items-baseline justify-between gap-3 mb-2">
      <h4 class="text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
        Value · {{ marks.length }} {{ marks.length === 1 ? 'mark' : 'marks' }}
      </h4>
    </div>

    <ul class="border border-border rounded-ds-lg overflow-hidden">
      <li
        v-for="(mark, index) in marks"
        :key="`${mark.date}-${index}`"
        class="flex flex-col sm:flex-row sm:items-center gap-4 px-4 py-2.5"
        :class="[
          mark === carrying ? 'bg-accent' : 'bg-card',
          index < marks.length - 1 ? 'border-b border-border' : '',
        ]"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span
              class="text-sm font-bold"
              :class="mark === carrying ? 'text-accent-foreground' : 'text-foreground'"
            >
              {{ dateLabel(mark.date) }}
            </span>
            <DSBadge
              v-if="mark === carrying"
              tone="brand"
              variant="soft"
              size="sm"
              class="uppercase tracking-ds-caps bg-card"
            >
              carrying value
            </DSBadge>
          </div>
          <p
            class="text-xs font-medium"
            :class="mark === carrying ? 'text-accent-foreground' : 'text-muted-foreground'"
          >
            {{ markCaption(mark) }}
          </p>
        </div>

        <p
          class="text-base font-bold tracking-ds-snug"
          :class="mark === carrying ? 'text-accent-foreground' : 'text-foreground'"
        >
          {{ money(mark.close) }}
        </p>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { DSBadge } from '@ds'

import { FRACTION_DIGITS } from '@/const'

import { getCurrencySymbol } from '@/helpers/common'
import { stringToLocalDateString } from '@/helpers/dates'
import { numberFormat } from '@/helpers/numbers'

import { carryingMark, markCaption, sortedMarks } from '../utils/helpers'
import { WizardMark } from '../utils/types'

/*
 * What the valuation step recorded, read back on the summary.
 *
 * The same list and the same two facts the step draws — which values there are,
 * and which of them the asset actually carries — without the controls: adding
 * and removing a mark belongs to the step that owns them, and a summary that
 * offered both would be a second place to do the same work.
 *
 * Nothing at all when nothing was recorded — the caller draws this only for a
 * list with something in it. An optional step left alone has no result to show,
 * and a panel saying so is a row of furniture on the one screen that should be
 * only what the wizard will create.
 */
const { marks: recorded, currency } = defineProps<{
  /** What the step recorded, in whatever order it was entered. */
  marks: WizardMark[]
  /** The wizard's currency, for the figures. */
  currency?: string
  /** The valuation step's own label, so a rename cannot leave this quoting it. */
  stepLabel: string
}>()

defineEmits<{
  (e: 'edit'): void
}>()

const marks = computed(() => sortedMarks(recorded))

const carrying = computed(() => carryingMark(recorded))

const dateLabel = (date: string) => stringToLocalDateString(date) ?? date

const money = (close: number) =>
  numberFormat(close, {
    currency: getCurrencySymbol(currency),
    fractionDigits: FRACTION_DIGITS,
  })
</script>
